import classNames from 'classnames';
import styles from './Form.module.scss';
import { UI_Heading } from 'ui/components/1-atoms/Heading/Heading';
import { UI_Picture } from 'ui/components/1-atoms/Media/Picture/Picture';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import { UI_TopLevelGrid } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGrid';
import { UI_TopLevelGridCell } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGridCell';
import { UI_Grid } from 'ui/components/4-habitats/Grid/Grid';
import { UI_GridCell } from 'ui/components/4-habitats/Grid/GridCell';
import ColorOptions, { GridLayoutTypeOptions } from 'helpers/types';
import { UI_Kicker, UI_KickerProps } from 'ui/components/1-atoms/Kicker/Kicker';
import { UI_HubSpotForm } from 'ui/components/2-molecules/HubSpotForm/HubSpotForm';

export interface UI_FormProps {
	bgColor?: ColorOptions;
	kicker: UI_KickerProps;
	heading: string;
	image?: {
		url: string;
		focalPoint?: { top: number; left: number };
		properties?: {
			altText?: string;
			umbracoExtension?: string;
		};
	};
	formHtmlCode: string;
	showImage?: boolean;
	layoutType?: GridLayoutTypeOptions;
	displayButtonRight?: boolean;
	selectAllLabel?: string;
	placeholderLabel?: string;
	inputFullWidth?: boolean;
	buttonStyle?: 'primary' | 'secondary';
}

export const UI_Form: React.FC<UI_FormProps> = ({
	bgColor = ColorOptions.White,
	kicker,
	heading,
	image,
	formHtmlCode,
	showImage = false,
	layoutType,
	displayButtonRight,
	selectAllLabel,
	placeholderLabel,
	inputFullWidth,
	buttonStyle = 'secondary',
}) => {
	return (
		<div className={classNames(styles.Form, `u-color___${bgColor}`)}>
			<UI_Container width="Standard">
				<UI_TopLevelGrid container wrap>
					<UI_TopLevelGridCell desktopWidth="16col" mobileWidth="100">
						{kicker && <UI_Kicker className={styles.Form_trumpetHeading} {...kicker}></UI_Kicker>}

						{heading && (
							<UI_Heading className={styles.Form_heading} headingLevel="h2" style="xl">
								{heading}
							</UI_Heading>
						)}
					</UI_TopLevelGridCell>

					{showImage && (
						<UI_TopLevelGridCell desktopWidth="22col" mobileWidth="100" desktopOffset="0col">
							<UI_Grid container wrap largeGutter>
								{layoutType === 'oneThirdImage' ? (
									<>
										<UI_GridCell desktopWidth="33" mobileWidth="100">
											{image && (
												<UI_Picture
													className={styles.Form_image}
													url={image.url}
													focalPoint={image.focalPoint}
													altText={image.properties.altText}
													sizes="(max-width: 768px) 100vw, (max-width: 1728px) 66vw, 1150px"
													fileType={image.properties.umbracoExtension}
												/>
											)}
										</UI_GridCell>
										<UI_GridCell desktopWidth="66" mobileWidth="100" className="hubspot">
											<UI_HubSpotForm
												className="two-thirds"
												formHtmlCode={formHtmlCode}
												displayButtonRight={displayButtonRight}
												inputFullWidth={inputFullWidth}
												selectAllLabel={selectAllLabel}
												placeholderLabel={placeholderLabel}
												buttonStyle={buttonStyle}
												bgColor={bgColor}
												kicker={kicker}
												showImage={showImage}
												layoutType={layoutType}
											/>
										</UI_GridCell>
									</>
								) : layoutType === 'halfImage' ? (
									<>
										<UI_GridCell desktopWidth="50" mobileWidth="100">
											{image && (
												<UI_Picture
													className={styles.Form_image}
													url={image.url}
													altText={image.properties.altText}
													sizes="(max-width: 768px) 100vw, (max-width: 1728px) 66vw, 1150px"
													fileType={image.properties.umbracoExtension}
												/>
											)}
										</UI_GridCell>
										<UI_GridCell desktopWidth="50" mobileWidth="100">
											<UI_HubSpotForm
												className="half"
												formHtmlCode={formHtmlCode}
												displayButtonRight={displayButtonRight}
												inputFullWidth={inputFullWidth}
												selectAllLabel={selectAllLabel}
												placeholderLabel={placeholderLabel}
												buttonStyle={buttonStyle}
												bgColor={bgColor}
												kicker={kicker}
												showImage={showImage}
												layoutType={layoutType}
											/>
										</UI_GridCell>
									</>
								) : (
									<>
										<UI_GridCell desktopWidth="66" mobileWidth="100">
											{image && (
												<UI_Picture
													className={styles.Form_image}
													url={image.url}
													altText={image.properties.altText}
													sizes="(max-width: 768px) 100vw, (max-width: 1728px) 66vw, 1150px"
													fileType={image.properties.umbracoExtension}
												/>
											)}
										</UI_GridCell>
										<UI_GridCell desktopWidth="33" mobileWidth="100">
											<UI_HubSpotForm
												formHtmlCode={formHtmlCode}
												displayButtonRight={displayButtonRight}
												inputFullWidth={inputFullWidth}
												selectAllLabel={selectAllLabel}
												placeholderLabel={placeholderLabel}
												buttonStyle={buttonStyle}
												bgColor={bgColor}
												kicker={kicker}
												showImage={showImage}
												layoutType={layoutType}
											/>
										</UI_GridCell>
									</>
								)}
							</UI_Grid>
						</UI_TopLevelGridCell>
					)}

					{!showImage && (
						<UI_TopLevelGridCell desktopWidth="24col" mobileWidth="100" desktopOffset="0col">
							<UI_Grid container wrap>
								<UI_GridCell desktopWidth="100" mobileWidth="100">
									<UI_HubSpotForm
										className="hubspot-noImage"
										formHtmlCode={formHtmlCode}
										displayButtonRight={displayButtonRight}
										inputFullWidth={inputFullWidth}
										selectAllLabel={selectAllLabel}
										placeholderLabel={placeholderLabel}
										buttonStyle={buttonStyle}
										bgColor={bgColor}
										kicker={kicker}
										showImage={showImage}
										layoutType={layoutType}
									/>
								</UI_GridCell>
							</UI_Grid>
						</UI_TopLevelGridCell>
					)}
				</UI_TopLevelGrid>
			</UI_Container>
		</div>
	);
};
